import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

// T070 — Small modal prompting the operator for a reason when dismissing
// one or more recommendations. The reason is captured verbatim (presets
// are quick-pick affordances, but the operator can type anything). Sent
// to the backend as `dismissedReason` on the PATCH /recommendations/:id body.
//
// Works for both single dismiss and bulk dismiss: the parent passes a `count`.
//
// T131 — built on the shared <Modal> (bare mode, size="sm" = the original
// max-w-md, modal z-tier). Modal owns the backdrop, focus trap, Escape, and
// initial focus (the textarea via initialFocusRef); we keep only the
// ⌘/Ctrl+Enter shortcut.

// T220 — exported so the dashboard's number-key shortcuts use the SAME six
// reasons the modal offers. Re-typing them somewhere else is how the copy in
// analyticsFunnel.js drifted out of sync with this one.
export const PRESETS = [
  // First, because it is the one reason that is about the POSTING rather than
  // the fit — the operator reaches for it when a two-month-old row turns out to
  // be closed, and that is the most common thing he does to an old queue.
  'Posting is no longer live',
  'Wrong location',
  'Salary too low',
  'Wrong seniority level',
  'Not the right company',
  'Not interested anymore',
  'Already applied elsewhere',
];

export default function DismissReasonModal({ count, jobTitle, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // T290b — this listener was registered once with `[]` and captured `submit`
  // from the first render, where `reason` is still ''. So the shortcut the panel
  // ADVERTISES ("⌘/Ctrl+Enter to confirm") always submitted an empty reason and
  // silently discarded whatever the operator had typed. The button worked, which
  // is why it went unnoticed. The eslint-disable underneath was suppressing the
  // exact warning that describes this.
  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      setSubmitting(false);
    }
  }, [onConfirm, reason]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submit]);

  const headerLabel =
    count > 1 ? `Dismiss ${count} jobs?` : jobTitle ? `Dismiss "${jobTitle}"?` : 'Dismiss this job?';

  return (
    <Modal
      size="sm"
      labelledById="dismiss-reason-title"
      initialFocusRef={inputRef}
      onClose={onCancel}
    >
      <header className="border-b border-slate-800 p-4">
        <p className="text-[10px] uppercase tracking-wide text-brand-500">Dismiss reason</p>
        <h2 id="dismiss-reason-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
          {headerLabel}
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Helps future-you (and the scorer eventually) remember why this didn't fit. Optional — leave blank to skip.
        </p>
      </header>

      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setReason(p)}
              className={
                'rounded-full border px-2.5 py-0.5 text-xs transition ' +
                (reason === p
                  ? 'border-brand-500 bg-brand-600/20 text-brand-300'
                  : 'border-slate-700 text-slate-300 hover:border-brand-600 hover:text-brand-400')
              }
            >
              {p}
            </button>
          ))}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-400">
            Reason (or pick one above)
          </label>
          <textarea
            ref={inputRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder='e.g. "Stack is .NET only — not my path"'
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-slate-500">{reason.length}/500 · ⌘/Ctrl+Enter to confirm</p>
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-400 hover:border-slate-500 hover:text-slate-200 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-600 disabled:opacity-50"
        >
          {submitting ? 'dismissing…' : reason.trim() ? 'Dismiss with reason' : 'Dismiss without reason'}
        </button>
      </footer>
    </Modal>
  );
}
